import { DC_Domain_Models_Products_DiscountType, DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

import {
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_MOVING_OUT,
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_SWITCH,
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT,
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX_URGENT,
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_MOVING_OUT,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_SWITCH,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX_URGENT,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_WARMTH,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_WATER,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_WARMTH,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_WATER,
  ENECO_SME_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
  ENECO_SME_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
  ENECO_SME_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
  ENECO_SME_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN,
} from './constants';
import { EnergyType, Offer } from '../types';

export const energyTypeIncludes = (energyType: EnergyType, type: EnergyType): boolean =>
  new RegExp(`${type}`, 'i').test(energyType);

export const checkOfferIncludesDynamicPricingProduct = (offer?: Offer): boolean =>
  !!offer?.products.some(
    product => (product.type === 'gas' || product.type === 'electricity') && product?.contractSpan?.duration === 0,
  );

export const checkOfferIncludesNonDynamicPricingProduct = (offer?: Offer): boolean =>
  !!offer?.products.some(
    product => (product.type === 'gas' || product.type === 'electricity') && product?.contractSpan?.duration !== 0,
  );

export const checkOffersIncludesDynamicPricingProduct = (offers: Offer[]): boolean => {
  return offers.some(offer => checkOfferIncludesDynamicPricingProduct(offer));
};

const byLabel = <T>(label: DC_Repositories_Base_Enumerations_Label, enecoValue: T, oxxioValue: T): T =>
  label === 'eneco' ? enecoValue : oxxioValue;

export const getMinContractStartDateInDays = (
  isMoving: boolean,
  isUrgent: boolean,
  isSME: boolean,
  label: DC_Repositories_Base_Enumerations_Label,
): number => {
  if (isSME) return ENECO_SME_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN;

  if (isUrgent) {
    return byLabel(
      label,
      ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT,
      OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT,
    );
  }

  if (isMoving) {
    return byLabel(
      label,
      ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_MOVING_OUT,
      OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_MOVING_OUT,
    );
  }

  return byLabel(
    label,
    ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_SWITCH,
    OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_SWITCH,
  );
};

export const getMaxContractStartDateInDays = (label: DC_Repositories_Base_Enumerations_Label) =>
  label === 'eneco'
    ? ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX
    : OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX;

export const getMinUrgentContractStartDateInDays = (label: DC_Repositories_Base_Enumerations_Label) =>
  label === 'eneco'
    ? ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT
    : OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT;

export const getMaxUrgentContractStartDateInDays = (label: DC_Repositories_Base_Enumerations_Label) =>
  label === 'eneco'
    ? ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX_URGENT
    : OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX_URGENT;

type MaxUsageType = 'ELECTRICITY_HIGH' | 'ELECTRICITY_LOW' | 'GAS' | 'WARMTH' | 'WATER';

// `sme` is only defined for the types that have a dedicated SME maximum; the others fall
// back to the label specific value regardless of segment.
const MAX_USAGE_BY_TYPE: Record<MaxUsageType, { eneco: number; oxxio: number; sme?: number }> = {
  ELECTRICITY_HIGH: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
    sme: ENECO_SME_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
  },
  ELECTRICITY_LOW: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
    sme: ENECO_SME_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
  },
  GAS: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
    sme: ENECO_SME_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
  },
  WARMTH: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_WARMTH,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_WARMTH,
  },
  WATER: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_WATER,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_WATER,
  },
};

export const getMaxUsageValue = (
  type: MaxUsageType,
  isSME: boolean,
  label: DC_Repositories_Base_Enumerations_Label,
): number => {
  const values = MAX_USAGE_BY_TYPE[type];
  if (!values) return 0;
  if (isSME && values.sme !== undefined) return values.sme;

  return byLabel(label, values.eneco, values.oxxio);
};

export const checkAvailabilityOfferType = (
  offers: Offer[],
  offerType?: DC_Domain_Models_Products_DiscountType,
): DC_Domain_Models_Products_DiscountType | undefined => offers.find(offer => offer.type === offerType)?.type;

export const selectDefaultOfferType = (offers: Offer[]): DC_Domain_Models_Products_DiscountType | undefined =>
  (offers.find(offer => offer.costDiscounts?.cashback || offer.mostPopularChoice) ?? offers[0])?.type;
